import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Check, Compass, Copy, Maximize2, Smartphone, Zap } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { pwa } from '../../lib/pwa';
import {
  SafariBottomBarShot,
  SafariShareMenuShot,
  AddConfirmShot,
  HomeScreenShot,
} from './IosSafariScreenshots';

/**
 * IosPwaTutorialModal — carousel edition (Jun 2026).
 * One step at a time, big illustration, short text, big Next button.
 * Follows the official Apple flow (iOS 17/18): Safari → Share →
 * Add to Home Screen → Add. If the user is on iOS but NOT in Safari
 * (Chrome/Firefox/Edge/in-app browsers), a dedicated screen guides
 * them to open the site with Safari first.
 */
const IosPwaTutorialModal = ({ open, onOpenChange, onLater, onGotIt }) => {
  const { t } = useTranslation('pwa');
  const [step, setStep] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(0);
      setCopied(false);
    }
  }, [open]);

  const needsSafari = pwa.isIos() && !pwa.isIosSafari();

  const steps = [
    { shot: <SafariBottomBarShot />, caption: t('tutorial.s1.caption'), highlight: t('tutorial.s1.highlight'), hint: t('tutorial.s1.hint') },
    { shot: <SafariShareMenuShot />, caption: t('tutorial.s2.caption'), highlight: t('tutorial.s2.highlight'), hint: t('tutorial.s2.hint') },
    { shot: <AddConfirmShot />, caption: t('tutorial.s3.caption'), highlight: t('tutorial.s3.highlight'), hint: t('tutorial.s3.hint') },
    { shot: <HomeScreenShot />, caption: t('tutorial.s4.caption'), highlight: t('tutorial.s4.highlight'), hint: t('tutorial.s4.hint') },
  ];
  const isLast = step === steps.length - 1;
  const current = steps[step];

  const perks = [
    { icon: Zap, label: t('tutorial.perks.speed') },
    { icon: Maximize2, label: t('tutorial.perks.fullscreen') },
    { icon: Smartphone, label: t('tutorial.perks.reach') },
  ];

  const copyLink = async () => {
    const url = window.location.origin;
    try {
      await navigator.clipboard.writeText(url);
    } catch (_e) {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_e2) { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] w-[calc(100vw-32px)] max-w-sm overflow-y-auto rounded-2xl border-amber-500/25 bg-neutral-950 p-5 text-foreground"
        data-testid="ios-pwa-tutorial-modal"
      >
        {needsSafari ? (
          <div data-testid="ios-pwa-safari-required">
            <DialogHeader>
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/15">
                <Compass className="h-8 w-8 gold-text" strokeWidth={1.6} aria-hidden="true" />
              </div>
              <DialogTitle className="mt-3 text-center text-xl font-bold">
                {t('tutorial.safari.title')}
              </DialogTitle>
              <DialogDescription className="mx-auto mt-1 max-w-xs text-center text-sm leading-relaxed text-muted-foreground">
                {t('tutorial.safari.text')}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-2.5">
              {[t('tutorial.safari.step1'), t('tutorial.safari.step2'), t('tutorial.safari.step3')].map((label, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400 text-sm font-bold text-black">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-foreground/90">{label}</span>
                </div>
              ))}
            </div>
            <Button
              type="button"
              onClick={copyLink}
              className="mt-4 h-12 w-full rounded-full gold-gradient text-base font-bold text-black"
              data-testid="ios-pwa-copy-link-btn"
            >
              {copied ? (
                <><Check className="mr-2 h-5 w-5" aria-hidden="true" />{t('tutorial.safari.copied')}</>
              ) : (
                <><Copy className="mr-2 h-5 w-5" aria-hidden="true" />{t('tutorial.safari.copy')}</>
              )}
            </Button>
            <button
              type="button"
              onClick={onGotIt}
              className="mx-auto mt-3 block text-sm text-muted-foreground transition-colors hover:text-foreground"
              data-testid="ios-pwa-safari-close"
            >
              {t('tutorial.got_it')}
            </button>
          </div>
        ) : (
          <div data-testid="ios-pwa-tutorial-carousel">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-center gap-2 text-center text-lg font-bold">
                <Smartphone className="h-5 w-5 gold-text" aria-hidden="true" />
                <span>{t('tutorial.title')}</span>
              </DialogTitle>
              <DialogDescription
                className="text-center text-[11px] font-bold uppercase tracking-[0.18em] gold-text"
                data-testid="ios-pwa-step-counter"
              >
                {t('tutorial.step_of', { current: step + 1, total: steps.length })}
              </DialogDescription>
            </DialogHeader>

            <div key={step} className="mt-1 animate-in fade-in slide-in-from-right-4 duration-300" data-testid={`ios-pwa-carousel-step-${step + 1}`}>
              <div className="mx-auto w-full max-w-[200px]">{current.shot}</div>
              <p className="mt-4 text-center text-base text-muted-foreground">{current.caption}</p>
              <p className="mt-0.5 text-center text-xl font-bold leading-snug gold-text">{current.highlight}</p>
              {current.hint && (
                <p className="mx-auto mt-2 max-w-[270px] text-center text-xs leading-relaxed text-neutral-500">
                  {current.hint}
                </p>
              )}
              {isLast && (
                <div className="mx-auto mt-3 max-w-[280px] space-y-1.5 rounded-xl border border-amber-500/15 bg-amber-500/[0.04] p-3" data-testid="ios-pwa-tutorial-perks">
                  {perks.map(({ icon: Icon, label }) => (
                    <div key={label} className="flex items-center gap-2">
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-amber-500/15 gold-text">
                        <Icon className="h-3 w-3" aria-hidden="true" />
                      </div>
                      <span className="text-[12px] font-medium text-foreground/90">{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center justify-center gap-1.5" aria-hidden="true">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? 'w-6 bg-amber-400' : 'w-1.5 bg-white/20'}`}
                />
              ))}
            </div>

            <div className="mt-4 flex items-center gap-2">
              {step > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setStep((v) => Math.max(0, v - 1))}
                  className="h-12 shrink-0 rounded-full px-4 text-muted-foreground"
                  aria-label={t('tutorial.step_of', { current: step, total: steps.length })}
                  data-testid="ios-pwa-tutorial-back"
                >
                  <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                </Button>
              )}
              <Button
                type="button"
                onClick={() => (isLast ? onGotIt() : setStep((v) => v + 1))}
                className="h-12 flex-1 rounded-full gold-gradient text-base font-bold text-black"
                data-testid={isLast ? 'ios-pwa-tutorial-got-it' : 'ios-pwa-tutorial-next'}
              >
                {isLast ? (
                  t('tutorial.got_it')
                ) : (
                  <>{t('tutorial.next')} <ArrowRight className="ml-1.5 h-5 w-5" aria-hidden="true" /></>
                )}
              </Button>
            </div>
            {!isLast && (
              <button
                type="button"
                onClick={onLater}
                className="mx-auto mt-2.5 block text-xs text-muted-foreground transition-colors hover:text-foreground"
                data-testid="ios-pwa-tutorial-later"
              >
                {t('tutorial.later')}
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IosPwaTutorialModal;
